/**
 * Durable recovery after terminal / CHECKOUT_SESSION_INVALID_STATE checkout.
 * Survives replace-navigate to shop (sessionStorage flash; one-shot consume).
 * Recovery path: resetShopCart (when COMPLETED) + replace-nav + flash toast — no query param.
 */

import type { ClassifyCustomerErrorInput } from '../../shared/hooks/useCustomerErrorHandler.js';

export const CHECKOUT_TERMINAL_RECOVERY_MESSAGE_KEY =
  'errors.checkoutSessionInvalidState' as const;

const FLASH_STORAGE_KEY = 'rpapp.customer.checkoutTerminalRecoveryFlash.v1';

export type CheckoutTerminalRecoveryFlash = {
  readonly messageKey: typeof CHECKOUT_TERMINAL_RECOVERY_MESSAGE_KEY;
  readonly writtenAtMs: number;
};

export function writeCheckoutTerminalRecoveryFlash(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const payload: CheckoutTerminalRecoveryFlash = {
    messageKey: CHECKOUT_TERMINAL_RECOVERY_MESSAGE_KEY,
    writtenAtMs: Date.now(),
  };
  try {
    sessionStorage.setItem(FLASH_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // quota / private mode — shop redirect still proceeds without toast
  }
}

/**
 * One-shot read. Returns message key when a fresh flash exists; otherwise null.
 * Clears storage whenever a payload is present (even if stale/invalid).
 */
export function consumeCheckoutTerminalRecoveryFlash(
  nowMs: number = Date.now(),
  maxAgeMs: number = 60_000,
): typeof CHECKOUT_TERMINAL_RECOVERY_MESSAGE_KEY | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(FLASH_STORAGE_KEY);
    sessionStorage.removeItem(FLASH_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null || raw.length === 0) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<CheckoutTerminalRecoveryFlash>;
    if (parsed.messageKey !== CHECKOUT_TERMINAL_RECOVERY_MESSAGE_KEY) {
      return null;
    }
    const writtenAt =
      typeof parsed.writtenAtMs === 'number' && Number.isFinite(parsed.writtenAtMs)
        ? parsed.writtenAtMs
        : 0;
    if (writtenAt <= 0 || nowMs - writtenAt > maxAgeMs) {
      return null;
    }
    return CHECKOUT_TERMINAL_RECOVERY_MESSAGE_KEY;
  } catch {
    return null;
  }
}

/**
 * Shop mount: consume flash and surface via payment toast channel (payToastMessage).
 * Returns true when a toast was shown.
 */
export function surfaceCheckoutTerminalRecoveryToast(input: {
  readonly t: (key: string) => string;
  readonly reportVisibleError: (
    message: string,
    operation?: string,
    classify?: ClassifyCustomerErrorInput,
  ) => void;
}): boolean {
  const messageKey = consumeCheckoutTerminalRecoveryFlash();
  if (messageKey === null) {
    return false;
  }
  input.reportVisibleError(input.t(messageKey), 'checkoutTerminalRecovery', {
    kind: 'payment',
    checkoutBlocked: false,
  });
  return true;
}
